import React, { createContext, FC, memo, ReactNode, useContext, useState } from 'react';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { IUpsertAvailabilityCycleRequestDTO } from 'src/api/dtos';
import AvailabilityRepository from 'src/api/repositories/ess/v1/availability.repository';
import { IError } from 'src/api/types';
import { DATE_TIME_FORMAT } from 'src/shared/constants';
import { CycleTypeEnums, OperationTypeEnums, WeekDaysEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { getErrorMessage } from 'src/shared/utils';
import { getFirstDayOfWeek } from 'src/store/user/user.selectors';

type EmployeeAvailabilityAddCycleProps = {
  inputs: IUpsertAvailabilityCycleRequestDTO;
  isAddMode: boolean;
  adding: boolean;
  apiError: string;
  touched: FormikTouched<IUpsertAvailabilityCycleRequestDTO>;
  errors: FormikErrors<IUpsertAvailabilityCycleRequestDTO>;
  onDayOfWeekChange: (dayOfWeek: WeekDaysEnums) => void;
  onStartDateChange: (date: moment.Moment | null) => void;
  onAdd: () => void;
  onCancel: () => void;
  onSave: () => void;
};

const EmployeeAvailabilityAddCycleContext = createContext<EmployeeAvailabilityAddCycleProps>(
  {} as EmployeeAvailabilityAddCycleProps,
);

export const useEmployeeAvailabilityAddCycle = (): EmployeeAvailabilityAddCycleProps =>
  useContext<EmployeeAvailabilityAddCycleProps>(EmployeeAvailabilityAddCycleContext);

export const EmployeeAvailabilityAddCycleProvider: FC<{ children: ReactNode }> = memo(({ children }) => {
  const { t } = useTranslate();
  const firstDayOfWeek = useSelector(getFirstDayOfWeek);

  const [isAddCycle, setIsAddCycle] = useState(false);
  const [apiError, setApiError] = useState('');

  const [updateCycle, { isLoading }] = AvailabilityRepository.endpoints.useUpdateCycleMutation();

  Yup.setLocale({
    mixed: {
      required: t('global.requiredField'),
    },
  });

  const addCycleValidationSchema = Yup.object().shape({
    id: Yup.number().required(),
    dayOfWeek: Yup.mixed<WeekDaysEnums>()
      .test((value) => Object.values(WeekDaysEnums).includes(value as WeekDaysEnums))
      .required(),
    type: Yup.mixed<CycleTypeEnums>()
      .test((value) => Object.values(CycleTypeEnums).includes(value as CycleTypeEnums))
      .required(),
    operationType: Yup.mixed<OperationTypeEnums>()
      .test((value) => Object.values(OperationTypeEnums).includes(value as OperationTypeEnums))
      .required(),
    cycleStart: Yup.date(),
    validFrom: Yup.date().required(),
    cycleLength: Yup.number(),
  });

  const handleUpsertCycle = async () => {
    try {
      await updateCycle(inputs).unwrap();

      toast.info(t('availability.youHaveSuccessfullyAddedANewCycle'), { type: toast.TYPE.SUCCESS });
      setIsAddCycle(false);
      setApiError('');
      resetForm();
    } catch (err) {
      const error = err as IError;
      console.error('Submit Error', { ...error });
      const errorMessage = getErrorMessage(error, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
      setApiError(errorMessage);
    }
  };

  const {
    values: inputs,
    setFieldValue,
    handleSubmit,
    touched,
    errors,
    resetForm,
  } = useFormik<IUpsertAvailabilityCycleRequestDTO>({
    initialValues: {
      id: -1,
      dayOfWeek: firstDayOfWeek,
      type: CycleTypeEnums.WEEKLY, // Only weekly cycle is supported in ESS for add cycle
      operationType: OperationTypeEnums.ADD,
      cycleStart: undefined,
      validFrom: '',
      cycleLength: 7, // Only weekly cycle is supported in ESS for add cycle
    },
    validationSchema: addCycleValidationSchema,
    onSubmit: handleUpsertCycle,
  });

  const handleAdd = () => {
    setIsAddCycle(true);
  };

  const handleDayOfWeekChange = (dayOfWeek: WeekDaysEnums) => {
    setFieldValue('dayOfWeek', dayOfWeek);
  };

  const handleStartDateChange = (date: moment.Moment | null) => {
    if (!date) {
      return;
    }

    setFieldValue('validFrom', date.format(DATE_TIME_FORMAT));
  };

  const handleCancel = () => {
    resetForm();
    setApiError('');
    setIsAddCycle(false);
  };

  const value: EmployeeAvailabilityAddCycleProps = {
    inputs: inputs,
    isAddMode: isAddCycle,
    adding: isLoading,
    apiError,
    touched,
    errors,
    onDayOfWeekChange: handleDayOfWeekChange,
    onStartDateChange: handleStartDateChange,
    onAdd: handleAdd,
    onCancel: handleCancel,
    onSave: handleSubmit,
  };

  return (
    <EmployeeAvailabilityAddCycleContext.Provider value={value}>
      {children}
    </EmployeeAvailabilityAddCycleContext.Provider>
  );
});
